/**
 * Turn an API error into a message worth showing the user.
 *
 * The API returns Laravel-shaped errors: `{ message, errors?: { field: [msg] } }`.
 * Generic toasts like "Something went wrong!" hid real, actionable feedback
 * (e.g. "The description field is required."), so prefer the server's own
 * message and surface field-level validation errors when present.
 */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong!'): string {
  const response = (error as { response?: { status?: number; data?: unknown } } | undefined)?.response;
  const data = response?.data as
    | { message?: string; error?: string; errors?: Record<string, string[] | string> }
    | undefined;

  // Never surface a server-side failure message. In production Laravel renders
  // "Server Error", which is worse than our contextual fallback, and if
  // APP_DEBUG were ever enabled the raw exception (SQL, paths) would land in a
  // user-facing toast.
  if ((response?.status ?? 0) >= 500) {
    return fallback;
  }

  // Field-level validation errors (422) are the most useful thing we can show.
  if (data?.errors && typeof data.errors === 'object') {
    const messages = Object.values(data.errors)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter((value): value is string => typeof value === 'string' && value.length > 0);

    // Cap it: a form failing several fields at once would otherwise produce a
    // paragraph-long toast.
    if (messages.length > 0) {
      return messages.slice(0, 2).join(' ');
    }
  }

  if (typeof data?.message === 'string' && data.message.length > 0) {
    return data.message;
  }

  if (typeof data?.error === 'string' && data.error.length > 0) {
    return data.error;
  }

  if (response?.status === 403) {
    return 'You do not have permission to do that.';
  }

  if (response?.status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }

  return fallback;
}
